import React, { useEffect, useRef, useState } from "react";
import { useIdentityVerification, useNavigation } from "../../../hooks";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";

/**
 * 전화번호 유효성 검사
 */
const validatePhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^01[0-9]{8,9}$/;
  return phoneRegex.test(phone.replace(/-/g, ""));
};

/**
 * 전화번호 포맷팅 (010-1234-5678 형식)
 */
const formatPhoneNumber = (value: string): string => {
  const numbers = value.replace(/[^0-9]/g, "");
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
};

const IdentityVerificationScreen: React.FC = () => {
  const { goBack } = useNavigation();
  const { isLoading, error, startVerification } = useIdentityVerification();
  const [phoneNumber, setPhoneNumber] = useState("010-");
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 컴포넌트 로드 시 입력란에 포커스
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    // 010- 이하로 지워지지 않도록 보호
    if (formatted.length >= 4 || formatted.startsWith("010")) {
      setPhoneNumber(formatted.length < 4 ? "010-" : formatted);
    }
    setPhoneError(null);
  };

  const handleStartVerification = async () => {
    const rawPhone = phoneNumber.replace(/-/g, "");
    if (!validatePhoneNumber(rawPhone)) {
      setPhoneError("올바른 전화번호를 입력해주세요. (예: 010-1234-5678)");
      return;
    }
    await startVerification(rawPhone);
  };

  return (
    <div className="h-screen-dvh bg-white flex flex-col overflow-hidden">
      {/* 상단 헤더 */}
      <header className="bg-white px-4 py-3 flex items-center flex-shrink-0">
        <button
          onClick={goBack}
          className="flex items-center justify-center w-8 h-8 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Icon name="chevronLeft" className="text-gray-600" />
        </button>
      </header>

      {/* 메인 콘텐츠 */}
      <main className="flex-1 flex flex-col px-6 py-6 overflow-y-auto min-h-0">
        <div className="flex-1 flex flex-col justify-center max-w-sm mx-auto w-full">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-gray-800">
              내 의료기록을 불러오려면
              <br />
              본인인증이 필요해요
            </h2>
          </div>

          {/* 전화번호 입력 필드 */}
          <div className="mb-6 flex flex-col items-center w-full">
            <div className="w-full max-w-[320px] bg-gray-100 rounded-3xl px-6 py-4">
              <input
                ref={inputRef}
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="010-0000-0000"
                maxLength={13}
                disabled={isLoading}
                className="w-full bg-transparent text-center text-xl text-text-primary placeholder-gray-400 focus:outline-none"
              />
            </div>
            {phoneError && (
              <p className="mt-2 text-sm text-red-600">{phoneError}</p>
            )}
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm text-center">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <Button
              fullWidth
              size="lg"
              onClick={handleStartVerification}
              disabled={isLoading || !phoneNumber}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 py-4 text-base"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  인증 진행 중...
                </span>
              ) : (
                "본인인증 시작"
              )}
            </Button>
          </div>

          <div className="mt-8 text-center">
            <p className="text-xs text-gray-400 leading-relaxed">
              본인인증 시 입력하신 정보는
              <br />
              본인확인 및 로그인/회원가입 목적으로 사용되며,
              <br />
              관련 법령에 따라 안전하게 보호됩니다.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default IdentityVerificationScreen;

